/**
 * Palette colori + stili di layout per i PDF "brandizzati"
 * (Magic Menu e Piatti del Giorno).
 *
 * Ogni palette espone i 3 colori principali in HEX e in array RGB
 * (formato richiesto da jsPDF setFillColor / setTextColor).
 *
 * Ogni style espone i font, le dimensioni titolo, i layout ornament
 * e il "look" generale del PDF.
 */

const hexToRgb = (hex) => {
    const h = (hex || "").replace("#", "").trim();
    if (h.length !== 6) return [0, 0, 0];
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
};

/**
 * 8 preset palette. La chiave è il valore HEX del colore primario,
 * accettata anche dal backend (BRAND_COLOR_PRESETS).
 */
export const BRAND_PALETTES = [
    {
        id: "#1a2a44",
        label: "Navy & Oro",
        primary: "#1a2a44",
        accent: "#c9a961",
        paper: "#faf7f2",
        ink: "#22262e",
        muted: "#6e7684",
    },
    {
        id: "#0f172a",
        label: "Nero & Oro",
        primary: "#0f172a",
        accent: "#d4af37",
        paper: "#f7f4ec",
        ink: "#171821",
        muted: "#6b7280",
    },
    {
        id: "#7f1d1d",
        label: "Rosso vivo",
        primary: "#7f1d1d",
        accent: "#f3c969",
        paper: "#fdf6ec",
        ink: "#3b1212",
        muted: "#857068",
    },
    {
        id: "#1e3a2b",
        label: "Verde salvia",
        primary: "#1e3a2b",
        accent: "#c5a572",
        paper: "#f5f1e8",
        ink: "#1b271d",
        muted: "#6a7763",
    },
    {
        id: "#7c2d12",
        label: "Terracotta",
        primary: "#7c2d12",
        accent: "#e0a96d",
        paper: "#fbf4ea",
        ink: "#3c1810",
        muted: "#8d6f5c",
    },
    {
        id: "#0c4a6e",
        label: "Blu mare",
        primary: "#0c4a6e",
        accent: "#facc6b",
        paper: "#f3f7fb",
        ink: "#0a1a26",
        muted: "#557184",
    },
    {
        id: "#3f1d38",
        label: "Vinaccia",
        primary: "#3f1d38",
        accent: "#dfb56a",
        paper: "#faf2f4",
        ink: "#1a0a18",
        muted: "#7b6173",
    },
    {
        id: "#1f2937",
        label: "Grafite",
        primary: "#1f2937",
        accent: "#e5b551",
        paper: "#f4f4f4",
        ink: "#111418",
        muted: "#6c7280",
    },
];

export const BRAND_STYLES = [
    {
        id: "modern",
        label: "Moderno",
        description:
            "Sans-serif pulito, eyebrow numerati, tanto bianco, niente cornice.",
        font: { display: "helvetica", body: "helvetica" },
        titleStyle: "bold",
        titleSize: 32,
        nameStyle: "bold",
        nameSize: 15,
        priceStyle: "bold",
        eyebrowStyle: "bold",
        nameCase: "normal",
        spacing: 11,
        frame: "none",
        align: "left",
    },
    {
        id: "classic",
        label: "Classico",
        description:
            "Serif elegante, cornice a doppio filetto oro, titolo centrato.",
        font: { display: "times", body: "times" },
        titleStyle: "bolditalic",
        titleSize: 36,
        nameStyle: "bold",
        nameSize: 14,
        priceStyle: "bold",
        eyebrowStyle: "italic",
        nameCase: "normal",
        spacing: 10,
        frame: "double",
        align: "center",
    },
    {
        id: "vintage",
        label: "Vintage",
        description:
            "Serif con maiuscoletto, cornice a tratto spesso, atmosfera trattoria.",
        font: { display: "times", body: "times" },
        titleStyle: "bolditalic",
        titleSize: 34,
        nameStyle: "italic",
        nameSize: 14,
        priceStyle: "bold",
        eyebrowStyle: "bold",
        nameCase: "smallcaps",
        spacing: 12,
        frame: "thick",
        align: "center",
    },
    {
        id: "street",
        label: "Street Food",
        description:
            "Sans-serif maiuscolo, cornice spessa colorata, prezzi enfatici.",
        font: { display: "helvetica", body: "helvetica" },
        titleStyle: "bold",
        titleSize: 38,
        nameStyle: "bold",
        nameSize: 16,
        priceStyle: "bold",
        eyebrowStyle: "bold",
        nameCase: "upper",
        spacing: 9,
        frame: "block",
        align: "left",
    },
];

export const DEFAULT_PALETTE_ID = "#1a2a44";
export const DEFAULT_STYLE_ID = "classic";

/**
 * Famiglie di font supportate nativamente da jsPDF (no asset extra).
 * `family` è la stringa passata a `doc.setFont`.
 * `weight` è normal | bold | italic | bolditalic.
 */
export const FONT_OPTIONS = [
    { id: "serif-elegant", label: "Serif elegante (corsivo)", family: "times", weight: "bolditalic" },
    { id: "serif-italic", label: "Serif corsivo", family: "times", weight: "italic" },
    { id: "serif-normal", label: "Serif classico", family: "times", weight: "normal" },
    { id: "serif-bold", label: "Serif grassetto", family: "times", weight: "bold" },
    { id: "sans-normal", label: "Sans-serif normale", family: "helvetica", weight: "normal" },
    { id: "sans-bold", label: "Sans-serif grassetto", family: "helvetica", weight: "bold" },
    { id: "sans-italic", label: "Sans-serif corsivo", family: "helvetica", weight: "italic" },
    { id: "sans-bolditalic", label: "Sans-serif corsivo grassetto", family: "helvetica", weight: "bolditalic" },
    { id: "mono-normal", label: "Mono normale", family: "courier", weight: "normal" },
    { id: "mono-bold", label: "Mono grassetto", family: "courier", weight: "bold" },
];

/** Le 5 voci di testo personalizzabili nel wizard. */
export const FONT_SLOTS = [
    { key: "title", label: "Titolo" },
    { key: "subtitle", label: "Sottotitolo / Messaggio" },
    { key: "dish", label: "Nome piatto" },
    { key: "price", label: "Prezzo" },
    { key: "allergens", label: "Allergeni" },
];

/** Default font preset per ogni slot, per ciascuno dei 4 stili. */
const DEFAULT_FONTS_BY_STYLE = {
    modern: {
        title: "sans-bold",
        subtitle: "sans-normal",
        dish: "sans-bold",
        price: "sans-bold",
        allergens: "sans-normal",
    },
    classic: {
        title: "serif-elegant",
        subtitle: "serif-italic",
        dish: "serif-bold",
        price: "serif-bold",
        allergens: "serif-italic",
    },
    vintage: {
        title: "serif-elegant",
        subtitle: "serif-italic",
        dish: "serif-italic",
        price: "serif-bold",
        allergens: "serif-italic",
    },
    street: {
        title: "sans-bold",
        subtitle: "sans-bold",
        dish: "sans-bold",
        price: "sans-bold",
        allergens: "sans-normal",
    },
};

export const getFont = (id) =>
    FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0];

export function getDefaultFontsForStyle(styleId) {
    return DEFAULT_FONTS_BY_STYLE[styleId] || DEFAULT_FONTS_BY_STYLE.classic;
}

export const getPalette = (id) =>
    BRAND_PALETTES.find((p) => p.id === id) || BRAND_PALETTES[0];

export const getStyle = (id) =>
    BRAND_STYLES.find((s) => s.id === id) || BRAND_STYLES[1];

/**
 * Ritorna i colori della palette in formato RGB array (jsPDF-ready)
 * + metadati di stile + font risolti per ciascuno slot.
 */
export function resolveBrand({ paletteId, styleId, logoDataUrl, useLogo, fontOverrides }) {
    const palette = getPalette(paletteId);
    const style = getStyle(styleId);
    const defaults = getDefaultFontsForStyle(style.id);
    const fonts = {};
    for (const slot of FONT_SLOTS) {
        const id = (fontOverrides && fontOverrides[slot.key]) || defaults[slot.key];
        fonts[slot.key] = getFont(id);
    }
    return {
        useLogo: !!(useLogo && logoDataUrl),
        logoDataUrl: logoDataUrl || "",
        palette,
        style,
        fonts,
        rgb: {
            primary: hexToRgb(palette.primary),
            accent: hexToRgb(palette.accent),
            paper: hexToRgb(palette.paper),
            ink: hexToRgb(palette.ink),
            muted: hexToRgb(palette.muted),
        },
    };
}
